import apiClient from '../../../core/Api';
import { ContentResponse, ContentQueryParams } from '../../../data/types/content.interface';
import {
  AnalyticsQueryParams,
  PostQueryParams,
  PostReqParams,
  PostResponse,
  PostStatsProps,
} from '../../../data/types/contentMarketing.interface';
import { Calendar, Clock, Eye, Heart, TrendingUp, Users } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

class ContentMarketingService {
  static getFinalFiles = async (params: ContentQueryParams, type: string): Promise<PostResponse> =>
    apiClient
      .get(`${API_BASE_URL}/contents/marketing/final/${type}`, {
        params: {
          currentPage: params.page,
          perPage: params.perPage,
        },
      })
      .then((response) => {
        const { data } = response;
        return data;
      });

  static getContents = async (params: ContentQueryParams): Promise<PostResponse> =>
    apiClient.get(`${API_BASE_URL}/contents/marketing/all`, { params }).then((response) => {
      const { data } = response;
      return data;
    });

  static getContentDetails = async (
    assetContentTypeName: string,
    marketingContentId: number,
    platform: string
  ): Promise<ContentResponse> =>
    apiClient
      .get(`${API_BASE_URL}/contents/marketing/${marketingContentId}`, {
        params: { assetContentTypeName, platform },
      })
      .then((response) => {
        return response.data;
      });

  static updatePublishedContentDetails = async (
    assetContentTypeName: string,
    marketingContentId: number,
    platform: string,
    payload: any
  ): Promise<ContentResponse> =>
    apiClient
      .put(`${API_BASE_URL}/contents/marketing/${marketingContentId}`, {
        assetContentTypeName,
        platform,
        ...payload,
      })
      .then((response) => {
        return response.data;
      });

  static delete = async (
    assetContentTypeName: string,
    marketingContentId: number,
    platform: string
  ): Promise<ContentResponse> =>
    apiClient
      .delete(`${API_BASE_URL}/contents/marketing/${marketingContentId}`, {
        params: { assetContentTypeName, platform },
      })
      .then((response) => {
        return response.data;
      });

  static updateContentMarketingDetails = async (
    id: number,
    assetContentTypeName: string,
    platform: string,
    formData: FormData,
    config: any
  ): Promise<ContentResponse> => {
    return apiClient
      .post(`${API_BASE_URL}/contents/publish/${assetContentTypeName}/${platform}/${id}`, formData, config)
      .then((response) => {
        const { data } = response;
        return data;
      });
  };

  static publishMarketingDetails = async (
    id: number,
    assetContentTypeName: string,
    platform: string,
    params: PostReqParams
  ): Promise<ContentResponse> => {
    return apiClient
      .post(`${API_BASE_URL}/contents/publish/${assetContentTypeName}/${platform}/${id}`, params)
      .then((response) => {
        const { data } = response;
        return data;
      });
  };

  static fetchUploads = async (platform: string): Promise<any> => {
    return apiClient.get(`${API_BASE_URL}/marketing/contents/${platform}`).then((response) => {
      const { data } = response;
      return data;
    });
  };

  static getPlatformAnalytics = async (platform: string, filters: PostQueryParams): Promise<any> => {
    return apiClient.get(`${API_BASE_URL}/marketing/analytics/${platform}`, { params: filters }).then((response) => {
      const { data } = response;
      return data;
    });
  };

  static getPlatformStats = async (
    platform: string,
    trigger: string,
    filters: AnalyticsQueryParams
  ): Promise<PostStatsProps[]> => {
    return apiClient
      .get(`${API_BASE_URL}/marketing/analytics/stats/${platform}`, { params: filters })
      .then((response) => {
        const {
          avgReach,
          scheduledVideos,
          totalEngagement,
          posts,
          growth,
          trend,
          totalReach,
          totalFollowers,
          avgEngagementRate,
        } = response.data;
        if (trigger === 'dashboard') {
          const result = [
            { label: 'Total Posts', value: posts, change: growth, trend: trend, icon: Calendar },
            { label: 'Total Engagement', value: totalEngagement, change: growth, trend: trend, icon: Heart },
            { label: 'Scheduled', value: scheduledVideos, change: growth, trend: trend, icon: Clock },
            { label: 'Avg. Reach', value: avgReach, change: growth, trend: trend, icon: Eye },
          ];
          return result;
        }
        return [
          { label: 'Total Reach', value: totalReach, change: growth, trend: trend, icon: Eye },
          { label: 'Total Engagement', value: totalEngagement, change: growth, trend: trend, icon: Heart },
          { label: 'Total Followers', value: totalFollowers, change: growth, trend: trend, icon: Users },
          { label: 'Avg. Engagement Rate', value: avgEngagementRate, change: growth, trend: trend, icon: TrendingUp },
        ];
      });
  };

  static getMarketingAnalytics = async (filters: AnalyticsQueryParams): Promise<any> => {
    return apiClient.get(`${API_BASE_URL}/marketing/analytics`, { params: filters }).then((response) => {
      const { data } = response;
      return data;
    });
  };

  static getMarketingPlatformPerformances = async (filters: AnalyticsQueryParams): Promise<any> => {
    return apiClient.get(`${API_BASE_URL}/marketing/platform/performance`, { params: filters }).then((response) => {
      const { data } = response;
      return data;
    });
  };

  static getTopPlatformingPosts = async (filters: AnalyticsQueryParams): Promise<any> => {
    return apiClient
      .get(`${API_BASE_URL}/marketing/platform/performance/posts`, { params: filters })
      .then((response) => {
        const { data } = response;
        return data;
      });
  };

  static fetchHolidays = async (country: 'IN' | 'US', year: number) => {
     return apiClient
      .get(`${API_BASE_URL}/holidays`, { params: { country, year } })
      .then((response) => {
        const { data } = response;
        return data.data;
      });
  };

  static convertHolidayMap = (holidays: any[], color: string, type: string): Record<string, { name: string; color: string; type: string }> => {
    return holidays.reduce((acc, item) => {
      const date = item.date.iso.split('T')[0];

      acc[date] = {
        name: item.name,
        color,
        type,
      };

      return acc;
    }, {} as Record<string, any>);
  };

  static getAudienceDemographics = async (platform: string, params: any): Promise<any[]> => {
    return apiClient.get(`${API_BASE_URL}/marketing/analytics/audience/${platform}`, { params }).then((response) => {
      const { data } = response;
      return data;
    });
  };
}
export default ContentMarketingService;
